import { Component } from '@angular/core';
import { AuthService, AuthResponse } from '../../core/services/auth/auth.service';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AlertService } from '../../core/services/alert/alert.service';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { AuthStateService } from '../../core/services/auth-state/auth-state-service.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, CommonModule, TranslateModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent {
  form: FormGroup;
  showMfa = false;
  challengeToken = '';
  mfaCode = '';
  rememberDevice = false;
  countdown = 0;
  countdownInterval: any = null;
  mfaError = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private authStateService: AuthStateService
  ) {
    this.form = this.fb.group({
      email: ['', Validators.required],
      password: ['', Validators.required],
    });

    // Store redirect URL from query param (survives MFA + page refresh)
    const redirectParam = this.route.snapshot.queryParams['redirect'];
    if (redirectParam) {
      sessionStorage.setItem('login_redirect', redirectParam);
    }
  }

  onLogin(): void {
    if (!this.form.valid) {
      this.alertService.warning('swal_messages.login.please_complete_form');
      return;
    }

    this.alertService.loading('swal_messages.please_wait');
    this.authService.login(this.form.value).subscribe({
      next: (response: AuthResponse) => {
        Swal.close();

        if (this.authService.isMfaRequired(response)) {
          // MFA requerido — si teníamos un device_token guardado, ya no sirve
          if (this.authService.getDeviceToken()) {
            this.authService.clearDeviceToken();
          }
          this.challengeToken = response.data!.challenge_token!;
          this.showMfa = true;
          this.mfaError = '';
          this.mfaCode = '';
          this.startCountdown();
        } else {
          // Sesión concedida directamente
          this.authService.handleSuccessfulLogin(response, 'replace', this.form.value.email);
          this.redirectBasedOnRole();
        }
      },
      error: (error) => {
        Swal.close();
        if (error.code === 401) {
          this.alertService.warning('swal_messages.login.login_failed');
        } else if (error.code === 429) {
          this.alertService.warning('swal_messages.mfa.rate_limit');
        } else {
          this.alertService.warning('swal_messages.login.login_failed');
        }
      },
    });
  }

  onVerifyCode(): void {
    if (!this.mfaCode || this.mfaCode.length !== 6) {
      this.mfaError = 'swal_messages.mfa.mfa_code_invalid';
      return;
    }

    this.alertService.loading('swal_messages.please_wait');
    this.authService
      .verifyMfa({
        challenge_token: this.challengeToken,
        code: this.mfaCode,
        remember_device: this.rememberDevice || undefined,
        device_label: this.rememberDevice ? this.getDeviceLabel() : undefined,
      })
      .subscribe({
        next: (response: AuthResponse) => {
          Swal.close();
          this.authService.handleSuccessfulLogin(response, 'replace', this.form.value.email);
          this.redirectBasedOnRole();
        },
        error: (error) => {
          Swal.close();
          if (error.code === 429) {
            this.handleMfa429(error);
          } else {
            // Mensaje genérico para 400 (código incorrecto/caducado/intentos agotados)
            this.mfaError = 'swal_messages.mfa.mfa_code_invalid';
          }
        },
      });
  }

  onResendCode(): void {
    if (this.countdown > 0) return;

    this.alertService.loading('swal_messages.please_wait');
    this.authService.resendMfa(this.challengeToken).subscribe({
      next: () => {
        Swal.close();
        this.mfaError = '';
        this.mfaCode = '';
        this.alertService.success('swal_messages.mfa.mfa_code_sent');
        this.startCountdown();
      },
      error: (error) => {
        Swal.close();
        if (error.code === 429) {
          this.handleMfa429(error);
        } else {
          this.mfaError = 'swal_messages.mfa.mfa_code_invalid';
        }
      },
    });
  }

  onBackToLogin(): void {
    this.showMfa = false;
    this.challengeToken = '';
    this.mfaCode = '';
    this.mfaError = '';
    this.rememberDevice = false;
    this.clearCountdown();
  }

  private handleMfa429(error: any): void {
    if (error.error?.mfa) {
      // Cooldown del reenvío
      this.mfaError = 'swal_messages.mfa.mfa_resend_too_soon';
    } else {
      // Rate limit de ruta
      this.mfaError = 'swal_messages.mfa.rate_limit';
    }
  }

  private startCountdown(): void {
    this.clearCountdown();
    this.countdown = 60;
    this.countdownInterval = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        this.clearCountdown();
      }
    }, 1000);
  }

  private clearCountdown(): void {
    this.countdown = 0;
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private getDeviceLabel(): string {
    return navigator.userAgent.substring(0, 255);
  }

  private redirectBasedOnRole(): void {
    // Check for stored redirect (from AuthGuard, survives MFA)
    const savedRedirect = sessionStorage.getItem('login_redirect');
    if (savedRedirect) {
      sessionStorage.removeItem('login_redirect');
      this.router.navigateByUrl(savedRedirect);
      return;
    }

    this.router.navigateByUrl(this.authStateService.getRedirectUrl());
  }
}
